import { ReactNode } from "react";

import styles from "./Modal.module.scss";

/**
 * The properties for a {@link Modal} element.
 */
interface IModalProps {
    /**
     * The unique HTML ID for this specific modal. Defaults to an auto-generate ID unique within this session.
     */
    id?: string;

    /**
     * The text to show in the modal's title area above the message.
     *
     * Ignored if {@link header} is set.
     */
    title?: string;

    /**
     * The content to display within the modal. This is normally specified as elements nested within the <Modal> tag, not using the attribute directly.
     */
    children?: ReactNode;

    /**
     * The default pixel width of the modal element. Default 600. Supports specifying units as a string.
     */
    width?: number;

    /**
     * The default pixel height of the modal element. Default none, so it resizes to fit the content.
     */
    height?: number;

    /**
     * The maximum height of the modal element as a CSS rule like `80vh`. Default none.
     */
    maxHeight?: string;

    /**
     * The absolute left X pixel at which to position the modal. If omitted, defaults to 36% of the screen width.
     *
     * Ignored if {@link centerContent} is true.
     */
    left?: number;

    /**
     * The absolute top X pixel at which to position the modal. If omitted, defaults to 20% of the screen height.
     *
     * Ignored if {@link centerContent} is true.
     */
    top?: number;

    /**
     * Whether the user can drag the modal to reposition it. Default true.
     *
     * Ignored if {@link header} is set.
     */
    draggable?: boolean;

    /**
     * The default CSS styles to apply to the main element. Default none.
     *
     * These are overridden by auto-generated styles like `width`, and props like {@link top} and {@link centerContent} if they're set.
     */
    style?: React.CSSProperties;

    /**
     * Custom CSS classes (space-delimited) to add to the main element. Default none.
     */
    cssClass?: string;

    /**
     * Whether the overlay which disables interacting with elements behind the modal should be semi-transparent instead of invisible. Default false.
     */
    showOverlay?: boolean;

    /**
     * Whether to add an 'X' close button in the modal title area. Default false.
     *
     * Ignored if {@link header} is set.
     */
    showX?: boolean;

    /**
     * Whether to add a 3px border radius to the top corners of the modal. Default false.
     */
    roundTopCorner?: boolean;

    /**
     * A custom element to append to the bottom of the modal. Default none.
     */
    footer?: JSX.Element;

    /**
     * Whether to prevent the user from closing the modal by clicking the background behind it. Default false.
     */
    disableOverlayClose?: boolean;

    /**
     * A custom element to render instead of the standard modal header area. Default none.
     *
     * This disables title props like {@link title} and {@link draggable}.
     */
    header?: JSX.Element;

    /**
     * A callback to invoke when the modal is closed. Default none.
     */
    onClose?: () => void;

    /**
     * Whether to auto-position the modal in the horizontal and vertical center of the screen. Default false.
     */
    centerContent?: boolean;

    /**
     * Whether the modal uses a fixed position, so it doesn't move when the user scrolls the page. Default false.
     */
    fixedOverlayPosition?: boolean;

    /**
     * An identifier used to target this modal by Cypress tests, added as a `data-cy` attribute on the main element.
     */
    dataCy?: string;

    /**
     * Whether to disable auto scroll style in the modal and body. Default false.
     */
    disableAutoScroll?: boolean;

    /**
     * The zIndex value to be set to the overlay
     */
    overlayZIndex?: number;

    bodyStyle?: React.CSSProperties;

    /**
     * Whether to allow the modal content to be scrollable. Default false.
     * Sticky header and footer will be used in this case.
     */
    scrollableContent?: boolean;
}

class Modal extends React.Component<IModalProps, never> {
    private containmentId: string = "modalcontainment";
    private componentId: string;
    private isHighlighting: boolean = false;
    private isMouseDown: boolean = false;

    constructor(props: IModalProps) {
        super(props);
        this.componentId = this.props.id || sessionStore.newId().toString();
    }

    public componentDidMount(): void {
        let drag = this.props.draggable ?? true;
        if (drag) {
            let elem = $("#" + this.componentId);
            let cnt = document.getElementById(this.containmentId);

            let dragOptions = {
                containment: cnt,
                handle: ".dragHandle",
                stop: function() {
                    elem.draggable("destroy");
                    elem.css("height", "auto");
                    window.setTimeout(() => elem.draggable(dragOptions), 50);
                }
            };
            elem.draggable(dragOptions);
            $(window).trigger("resize");
        }
    }

    public componentWillUnmount(): void {
        let drag = this.props.draggable ?? true;
        if (drag) {
            var elem = $("#" + this.componentId);
            elem.draggable().stop();
            elem.draggable("destroy");
        }
    }

    public render(): JSX.Element {
        let overlayStyle: React.CSSProperties = {
            position: this.props.fixedOverlayPosition ? "fixed" : "absolute",
            left: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            zIndex: this.props.overlayZIndex ?? 999
        };

        let bodyStyle: React.CSSProperties = {
            ...this.props.bodyStyle,
            maxHeight: "100vh"
        };

        if (this.props.showOverlay)
            overlayStyle.backgroundColor = "rgba(0, 0, 0, 0.45)";

        if (this.props.centerContent)
            overlayStyle = { ...overlayStyle, display: "flex", justifyContent: "center", alignItems: "center" };

        let popupStyle: React.CSSProperties = this.props.style ?? {};

        const width = this.props.width ? this.props.width : 600;
        popupStyle.width = width + "px";
        popupStyle.height = this.props.height ? this.props.height + "px" : "";

        if (!this.props.centerContent) {
            popupStyle.left = this.props.left ? this.props.left + "px" : `calc(50% - ${width / 2}px)`;
            popupStyle.top = this.props.top ? this.props.top + "px" : "20%";
        }

        if (this.props.roundTopCorner) {
            popupStyle.borderTopLeftRadius = "3px";
            popupStyle.borderTopRightRadius = "3px";
        }

        if (this.props.maxHeight) {
            bodyStyle.maxHeight = this.props.maxHeight;
        }

        return (
            <div
                id={this.containmentId}
                onMouseMove={() => this.handleMouseMove()}
                onMouseUp={() => this.handleMouseUp()}
                onMouseDown={() => this.handleMouseDown()}
                onClick={e => this.handleOutsidePopupClick(e)}
                style={overlayStyle}
            >
                <div
                    id={this.componentId}
                    className={`${styles.modal} ${this.props.disableAutoScroll ? styles.overflowUnset : ""} ${this.props.cssClass} ${this.props.scrollableContent ? styles.scrollable : ""}`}
                    onClick={e => e.stopPropagation()}
                    style={popupStyle}
                    data-cy={this.props.dataCy}
                >
                    {this.renderHeader()}
                    <div className={`modal-body ${styles.body}`} style={bodyStyle}>
                        {this.props.children}
                    </div>
                    {this.props.footer && (
                        <div className={styles.footer}>
                            {this.props.footer}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderHeader(): JSX.Element {
        if (this.props.header)
            return this.props.header;

        return (
            <div className={`${styles.header} ${this.props.draggable ? "dragHandle" : ""}`}>
                <div className={styles.title}>
                    {this.props.title ?? ""}
                </div>
                {this.props.showX && (
                    <span data-cy="modalHeader-closeButton" className={styles.close} onClick={() => this.closePopup()}>×</span>
                )}
            </div>
        );
    }

    private handleOutsidePopupClick(e: React.MouseEvent): void {
        e.stopPropagation();
        if (!this.props.disableOverlayClose && !this.isHighlighting)
            this.closePopup();

        this.isHighlighting = false;
    }

    private closePopup(): void {
        this.props.onClose?.();
    }

    private handleMouseUp(): void {
        this.isMouseDown = false;
    }

    private handleMouseMove(): void {
        if (this.isMouseDown) {
            this.isHighlighting = true;
        }
    }

    private handleMouseDown(): void {
        this.isMouseDown = true;
    }
}

export { Modal };
